import React, { useState, useMemo } from 'react'
import { Button, Stack, Typography, useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { SideContent } from 'pages/Contacts/List/Card/PreviewStyles'
import { toast } from 'components/global/CUK/Toast/Toast'

import { useSelection } from '../store'
import { useRemoveMulti, useDeactivateMulti } from '../use-actions'
import { FormActivate } from '../Forms'

const Header = () => {
  const theme = useTheme()
  const { t } = useTranslation()
  const [selection, setSelection] = useSelection()
  const navigate = useNavigate()

  return (
    <Stack justifyContent={'space-between'} direction={'row'} width={'100%'}>
      <Stack direction={'row'} gap={1}>
        <Stack
          sx={{
            width: 32,
            height: 32,
            borderRadius: 16,
            backgroundColor: '#DBE8FE',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
          <i className={'fa-solid fa-list-check'} style={{ color: theme.palette.primary.main }} />
        </Stack>
        <Stack>
          <Typography component={'span'} sx={{ fontSize: 10 }}>
            {t('bypass.card_in_mass.count', { count: selection.length })}
          </Typography>
          <Typography component={'span'} sx={{ fontSize: 14, fontWeight: 600 }}>
            {t('auth.card_in_mass.title')}
          </Typography>
        </Stack>
      </Stack>
      <i
        className={'fa-light fa-xmark'}
        style={{ cursor: 'pointer' }}
        onClick={() => {
          setSelection([])
          navigate('/bypass')
        }}
      />
    </Stack>
  )
}

const RemoveAction: React.FC<{ onError: (v: string | null) => void }> = ({ onError = () => { } }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [selection, setSelection] = useSelection()
  const [remove, { loading }] = useRemoveMulti()

  const handleClick = async () => {
    onError(null)
    const { error } = await remove(selection.map(({ id }) => id))
    if (error) {
      toast.error(t('bypass.remove.error', { count: selection.length }))
      onError(error as string)
    } else {
      setSelection([])
      toast.success(t('bypass.remove.success', { count: selection.length }))
      navigate('/bypass')
    }
  }
  return (
    <Button size={'small'} color={'error'} variant={'contained'} onClick={handleClick}>
      <i className={`fa-solid fa-${!loading ? 'trash' : 'spinner fa-spin'}`} style={{ marginRight: 8 }} />
      {
        loading ? t('ui_elements.in_progress') : t('ui_elements.remove')
      }
    </Button>
  )
}

const DeactivateAction: React.FC<{ onError: (v: string | null) => void }> = ({ onError = () => { } }) => {
  const [deactivate, { loading }] = useDeactivateMulti()
  const { t } = useTranslation()
  const [selection, setSelection] = useSelection()

  const handleClick = async () => {
    onError(null)
    const { error } = await deactivate(selection.map(({ id }) => id))
    if (error) {
      toast.error(t('bypass.deactivate.error', { count: 2 }))
      onError(error as string)
    } else {
      toast.success(t('bypass.deactivate.success', { count: 2 }))
      setSelection([])
    }
  }

  return (
    <Button size={'small'} color={'error'} variant={'outlined'} onClick={handleClick}>
      <i className={`fa-solid fa-${!loading ? 'pause' : 'spinner fa-spin'}`} style={{ marginRight: 8 }} />
      {loading ? t('ui_elements.in_progress') : t('bypass.actions.deactivate')}
    </Button>
  )
}

const ActivateAction = () => {
  const { t } = useTranslation()
  const [, setSelection] = useSelection()
  const [showForm, setShowForm] = useState(false)

  return (
    <>
      <Button size={'small'} color={'success'} variant={'outlined'} onClick={() => setShowForm(true)}>
        <i className={'fa-solid fa-play'} style={{ marginRight: 8 }} />
        {t('bypass.actions.activate')}
      </Button>
      {
        showForm && (
          <FormActivate
            onClose={() => setShowForm(false)}
            onSuccess={() => {
              setSelection([])
            }} />
        )
      }
    </>

  )
}

const CardInMass = () => {
  const [selection, setSelection] = useSelection()
  const [error, setError] = useState<string | null>(null)
  const navigate = useNavigate()
  const theme = useTheme()
  const { t } = useTranslation()
  const onlyActivate = useMemo(() => {
    return selection.every(bypass => !bypass.active)
  }, [selection])
  const onlyDeactivate = useMemo(() => {
    return selection.every(bypass => bypass.active)
  }, [selection])
  const canActivate = useMemo(() => {
    return (!onlyActivate && !onlyDeactivate) || onlyActivate
  }, [onlyActivate, onlyDeactivate])
  const canDeactivate = useMemo(() => {
    return (!onlyActivate && !onlyDeactivate) || onlyDeactivate
  }, [onlyActivate, onlyDeactivate])

  return (
    <SideContent style={{ width: 420, minHeight: 0 }}>
      <Stack
        gap={3}
        flex={1}
        sx={{
          minHeight: 0,
          height: '100%',
          border: '1px solid #EFEFEF',
          borderRadius: '24px',
          backgroundColor: 'white',
          boxSizing: 'border-box',
          overflow: 'hidden',
          p: 3
        }}
      >
        <Header />
        <Stack flex={1} minHeight={0} justifyContent={'space-between'}>
          <Stack
            gap={2}
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': {
                display: 'none'
              }
            }}
          >
            {
              Boolean(error) && (
                <Typography sx={{ fontSize: 12, fontWeight: 500, color: 'error.main' }}>
                  {t(`api_errors.${error}`)}
                </Typography>
              )
            }
            <Stack gap={1}>
              {
                selection.map(bypass => (
                  <Stack key={bypass.id} direction={'row'} justifyContent={'space-between'}>
                    <Typography component={'span'} sx={{ color: 'content.secondary', fontSize: 12 }}>
                      {bypass.mac_address}
                    </Typography>
                    <Stack
                      sx={{
                        width: 16,
                        height: 16,
                        backgroundColor: theme.palette.grey[200],
                        borderRadius: 10
                      }}
                      justifyContent={'center'}
                      alignItems={'center'}>
                      <i
                        className={'fa-solid fa-minus'}
                        style={{ cursor: 'pointer', fontSize: 12 }}
                        onClick={() => {
                          setSelection(prev => prev.filter(({ id }) => id !== bypass.id))
                        }}
                      />
                    </Stack>
                  </Stack>
                ))
              }
            </Stack>
          </Stack>
          <Stack direction={'row'} justifyContent={'space-between'} flexShrink={0}>
            <Button
              size={'small'}
              variant={'outlined'}
              onClick={() => {
                setSelection([])
                navigate('..')
              }}
            >
              {t('ui_elements.cancel')}
            </Button>
            <Stack direction={'row'} gap={1}>
              {
                canActivate && <ActivateAction />
              }
              {
                canDeactivate && <DeactivateAction onError={(e) => setError(e)} />
              }
              <RemoveAction onError={(e) => setError(e)} />
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </SideContent>
  )
}

export default CardInMass